import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function LoanRepaymentCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Loans", url: "https://calclab.app/loans" },
      { name: "Loan Repayment Calculator", url: "https://calclab.app/calculator/loan-repayment" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Loan Repayment Calculator",
      "Calculate monthly payment, total cost, and interest for loans. Free online finance calculator.",
      "https://calclab.app/calculator/loan-repayment",
      lang
    )
  ]);
  const [principal, setPrincipal] = useState("");
  const [rate, setRate] = useState("");
  const [years, setYears] = useState("");
  const [results, setResults] = useState<any>(null);

  const calculate = () => {
    const p = parseFloat(principal);
    const r = parseFloat(rate) / 100 / 12;
    const n = parseFloat(years) * 12;
    
    if (!p || !r || !n || p <= 0 || n <= 0) return;
    
    const monthly = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = monthly * n;
    const interest = total - p;
    
    setResults({
      monthly: monthly.toFixed(2),
      total: total.toFixed(2),
      interest: interest.toFixed(2)
    });
  };

  return (
    <PageLayout 
      calculatorName="Loan Repayment Calculator" 
      calculatorPath="/calculator/loan-repayment"
      category="finance"
      subcategory="Loans"
    >
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate Loan Payment</CardTitle>
            <CardDescription>Monthly payment and total cost</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Loan Amount ($)</Label>
              <Input type="number" value={principal} onChange={(e) => setPrincipal(e.target.value)} placeholder="10000" />
            </div>
            <div>
              <Label>Annual Interest Rate (%)</Label>
              <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="5" step="0.01" />
            </div>
            <div>
              <Label>Loan Term (years)</Label>
              <Input type="number" value={years} onChange={(e) => setYears(e.target.value)} placeholder="5" />
            </div>
            <Button onClick={calculate} className="w-full">Calculate</Button>
          </CardContent>
        </Card>

        {results && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Payment Schedule</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center mb-4">
                  <div className="text-5xl font-bold text-primary mb-2">${results.monthly}</div>
                  <div className="text-lg text-muted-foreground">Monthly Payment</div>
                </div>
                <div className="space-y-2 border-t pt-4">
                  <div className="flex justify-between text-lg">
                    <span>Total Amount Paid:</span>
                    <span className="font-semibold">${results.total}</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span>Total Interest:</span>
                    <span className="font-semibold text-red-500">${results.interest}</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span>Principal:</span>
                    <span className="font-semibold">${principal}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <CalculatorSuperCalcCTA config={calculatorCTAs["loan-repayment"]} trigger={results.monthly} />
          </>
        )}
      </div>
    </PageLayout>
  );
}
